import {
    withHeaders,
    instance as axiosInstance,
} from '../core/util/axiosInstance';
import { getDefaultHeaders } from '../core/util/other';
import { MutationFunction, UseMutationResult, useMutation } from 'react-query';
import { AxiosResponse } from 'axios';
import { commonShowAlert } from '../lib';
import { useAlert } from '../core';

interface CreateSystemWalletProps {
    description: string;
    type: string;
    writeUserGroup: string;
    accessControlUserGroup: string;
    token: string;
}
interface EditSystemWalletProps {
    id: string;
    description: string;
    type: string;
    writeUserGroup: string;
    accessControlUserGroup: string;
    token: string;
}

interface DeleteSystemWalletProps {
    id: string;
    token: string;
}

export const createSystemWallet: MutationFunction<
    AxiosResponse<any>,
    CreateSystemWalletProps
> = async (props: CreateSystemWalletProps) => {
    const { description, type, writeUserGroup, accessControlUserGroup, token } =
        props;
    const headers = getDefaultHeaders(token);
    const url = '/metadata/system/wallet';

    return axiosInstance().post(
        url,
        {
            description,
            type,
            writeUserGroup,
            accessControlUserGroup,
        },
        withHeaders(headers)
    );
};

export const editSystemWallet: MutationFunction<
    AxiosResponse<any>,
    EditSystemWalletProps
> = async (props: EditSystemWalletProps) => {
    const {
        description,
        type,
        writeUserGroup,
        accessControlUserGroup,
        token,
        id,
    } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/system/wallet/${id}`;

    return axiosInstance().put(
        url,
        {
            description,
            type,
            writeUserGroup,
            accessControlUserGroup,
        },
        withHeaders(headers)
    );
};

export const deleteSystemWallet: MutationFunction<
    AxiosResponse<any>,
    DeleteSystemWalletProps
> = async (props: DeleteSystemWalletProps) => {
    const { token, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/system/wallet/${id}`;

    return axiosInstance().delete(url, withHeaders(headers));
};

export const useSystemWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    CreateSystemWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(createSystemWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Created');
        },
        onError: (error: any) => {
            let errorMessage: string =
                error?.data?.message ?? 'Unable to create system wallet';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useUpdateSystemWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    EditSystemWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(editSystemWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Updated');
        },
        onError: (error: any) => {
            let errorMessage: string =
                error?.data?.message ?? 'Unable to edit system wallet';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useDeleteSystemWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    DeleteSystemWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(deleteSystemWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Deleted');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to delete system wallet';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
